import type { TFunction } from "react-i18next";
import type { Api } from "../../services/api";
import { getItemTranslation } from "../app/utils/getItemTranslation";

/**
 * How a journal's SIGAPS band is spoken, everywhere Juisci states one.
 *
 * The band itself is an administrative letter grade; what a reader sees is the
 * letter, the quartile it maps to, and the percentile phrase. Owned here rather
 * than by either surface: the article page's evidence sheet and JuisciGPT's
 * source sheet state the same rank, and a rank that reads differently depending
 * on where it is shown is the bug this prevents.
 *
 * Only the VOCABULARY lives here. The two surfaces draw the mark differently on
 * purpose (see `JournalBandBadge`), and their layouts are separate designs.
 */
type ArticleDetail =
  Api["get"]["/webapp/public/articles/:slug"]["output"]["body"];
export type JournalRank = NonNullable<ArticleDetail["journalRank"]>;
export type JournalBand = NonNullable<JournalRank["band"]>;

// The SIGAPS band, displayed as a bare rank letter (the band name itself is
// French administrative vocabulary the product does not surface).
export const BAND_LABEL = {
  a_plus: "A+",
  a: "A",
  b: "B",
  c: "C",
  d: "D",
  e: "E",
} as const satisfies Record<JournalBand, string>;

// Band quality order for picking the most favourable specialty band — mirrors
// BAND_SCORE in domain/public-quality-score.ts.
const BAND_ORDER = {
  a_plus: 0,
  a: 1,
  b: 2,
  c: 3,
  d: 4,
  e: 5,
} as const satisfies Record<JournalBand, number>;

// The band's IF-percentile range, spoken as the quartile clinicians already
// know from the journal rankings: a_plus/a/b sit in the top 25% (Q1), c = 25–50% (Q2),
// d = 50–75% (Q3), e = bottom 25% (Q4). Untranslated — Q1..Q4 is universal.
export const BAND_QUARTILE = {
  a_plus: "Q1",
  a: "Q1",
  b: "Q1",
  c: "Q2",
  d: "Q3",
  e: "Q4",
} as const satisfies Record<JournalBand, string>;

/** The indexes that positively vouch for the journal, in badge order. */
export function indexedIn(rank: JournalRank): string[] {
  return [...(rank.medline ? ["Medline"] : []), ...(rank.doaj ? ["DOAJ"] : [])];
}

/**
 * The band shown for THIS article: the most favourable band among the
 * article's own specialties (an article carries several; ties break on the
 * article's specialty order) — same most-favourable rule as the banding
 * itself. Falls back to the journal's best band with no named specialty when
 * the journal is not banded in any of the article's specialties.
 */
export function articleBand(
  specialties: ArticleDetail["medical_specialties"],
  rank: JournalRank
): { band: JournalBand; specialtyName: string | null } | null {
  let best: {
    band: JournalBand;
    specialtyName: string | null;
  } | null = null;
  for (const specialty of specialties) {
    const row = rank.bands.find((band) => band.specialtyId === specialty.id);
    if (row === undefined) continue;
    if (best === null || BAND_ORDER[row.band] < BAND_ORDER[best.band]) {
      best = {
        band: row.band,
        specialtyName: getItemTranslation(specialty),
      };
    }
  }
  if (best !== null) return best;
  if (rank.band === null) return null;
  return { band: rank.band, specialtyName: null };
}

/**
 * SIGN-OFF: the wording of each band, from the banding rule in
 * domain/journal/specialty-bands.ts (a = top 10% by IF, b = 10–25%, c = 25–50%,
 * d = 50–75%, e = bottom 25%; a_plus = the fixed flagship list). The caller
 * always supplies a specialty name (the article's own, or the signed-off
 * "General medicine" fallback), so a phrase always exists.
 */
export function bandPhrase(
  t: TFunction,
  band: JournalBand,
  specialty: string
): string {
  if (band === "a_plus") return t("Flagship journal");
  switch (band) {
    case "a":
      return t("Top 10% in {{specialty}}", { specialty });
    case "b":
      return t("Top 25% in {{specialty}}", { specialty });
    case "c":
      return t("Top 50% in {{specialty}}", { specialty });
    case "d":
      return t("Top 75% in {{specialty}}", { specialty });
    case "e":
      return t("Bottom 25% in {{specialty}}", { specialty });
    default:
      throw new Error(band satisfies never);
  }
}
